import React, { useEffect, useRef } from 'react';

export interface UseModalScrollLockOptions {
  isOpen: boolean;
  onClose?: () => void;
  containerRef?: React.RefObject<HTMLElement | null>;
}

export function useModalScrollLock({ isOpen, onClose, containerRef }: UseModalScrollLockOptions) {
  const savedScrollPos = useRef<number>(0);
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    // 1. Record current active element for focus restoration on close
    if (document.activeElement instanceof HTMLElement) {
      previousFocus.current = document.activeElement;
    }

    // 2. Save current page scroll position
    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    savedScrollPos.current = scrollY;

    // 3. Pause Lenis smooth scroll engine
    const lenis = (window as any).__lenis;
    if (lenis && typeof lenis.stop === 'function') {
      lenis.stop();
    }

    // 4. Save previous overflow styles
    const prevBodyOverflow = document.body.style.overflow;
    const prevDocOverflow = document.documentElement.style.overflow;

    // 5. Freeze scroll completely without altering layout positioning
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.classList.add('modal-open');

    // 6. Handle ESC key & Focus Trap
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (onClose) {
          e.preventDefault();
          onClose();
        }
        return;
      }

      if (e.key === 'Tab' && containerRef?.current) {
        const focusables = containerRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;

        const firstElement = focusables[0];
        const lastElement = focusables[focusables.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // 7. Auto-focus first focusable element inside modal container
    const autoFocusTimer = setTimeout(() => {
      if (containerRef?.current) {
        const focusables = containerRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length > 0) {
          focusables[0].focus();
        }
      }
    }, 60);

    // Clean up & restore scroll position on unmount / close
    return () => {
      clearTimeout(autoFocusTimer);
      window.removeEventListener('keydown', handleKeyDown);

      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevDocOverflow;
      document.documentElement.classList.remove('modal-open');

      if (lenis && typeof lenis.start === 'function') {
        lenis.start();
      }

      // Restore keyboard focus to triggering element
      if (previousFocus.current && typeof previousFocus.current.focus === 'function') {
        previousFocus.current.focus();
      }
    };
  }, [isOpen, onClose, containerRef]);
}
